import { useEffect, useState } from 'react';
import api from '@/lib/api';
import { PageHeader, EmptyState, LoadingShell } from '@/components/ui-bits';
import { UsersRound, Plus, X, Save, Trash2, Building2 } from 'lucide-react';
import { toast } from 'sonner';

export default function TeamsPage() {
  const [teams, setTeams] = useState(null);
  const [users, setUsers] = useState([]);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    const [t, u] = await Promise.all([
      api.get('/teams'),
      api.get('/admin/users'),
    ]);
    setTeams(t.data.teams || []);
    setUsers(u.data.users || []);
  };

  useEffect(() => { load(); }, []);

  if (!teams) return <LoadingShell />;

  const openNew = () => setEditing({
    name: '', kind: 'team', description: '', member_ids: [], leader_id: '', active: true,
  });

  const save = async () => {
    if (!editing.name?.trim()) return toast.error('Nombre requerido');
    try {
      const payload = {
        name: editing.name.trim(),
        kind: editing.kind || 'team',
        description: editing.description || null,
        member_ids: editing.member_ids || [],
        leader_id: editing.leader_id || null,
        active: editing.active !== false,
      };
      if (editing.id) {
        await api.patch(`/teams/${editing.id}`, payload);
        toast.success('Equipo actualizado');
      } else {
        await api.post('/teams', payload);
        toast.success('Equipo creado');
      }
      setEditing(null);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error');
    }
  };

  const del = async (t) => {
    if (!confirm(`¿Eliminar "${t.name}"?`)) return;
    try {
      await api.delete(`/teams/${t.id}`);
      toast.success('Eliminado');
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error');
    }
  };

  const toggleMember = (uid) => {
    setEditing((prev) => {
      const set = new Set(prev.member_ids || []);
      if (set.has(uid)) set.delete(uid);
      else set.add(uid);
      return { ...prev, member_ids: [...set] };
    });
  };

  return (
    <div>
      <PageHeader
        title="Equipos y sucursales"
        description="Define equipos reales. La visibilidad “Mi equipo” usa estos grupos (no solo el rol)."
        actions={(
          <button data-testid="team-new" onClick={openNew} className="btn-primary">
            <Plus className="h-4 w-4" /> Nuevo equipo
          </button>
        )}
      />

      {teams.length === 0 ? (
        <EmptyState
          icon={UsersRound}
          title="Sin equipos"
          description="Crea un equipo o sucursal y asigna miembros. Luego activa visibilidad “team” en Ajustes."
          cta={<button onClick={openNew} className="btn-primary">Crear equipo</button>}
        />
      ) : (
        <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-3">
          {teams.map((t) => (
            <div key={t.id} data-testid="team-card" className="surface p-4">
              <div className="flex items-start justify-between gap-2">
                <div>
                  <div className="flex items-center gap-2">
                    {t.kind === 'branch' ? <Building2 className="h-4 w-4 text-muted-foreground" /> : <UsersRound className="h-4 w-4 text-muted-foreground" />}
                    <h3 className="font-display font-semibold">{t.name}</h3>
                    <span className="chip text-[10px]">{t.kind === 'branch' ? 'Sucursal' : 'Equipo'}</span>
                    {t.active === false && <span className="chip text-[10px] text-muted-foreground">Inactivo</span>}
                  </div>
                  {t.description && <p className="text-xs text-muted-foreground mt-1">{t.description}</p>}
                </div>
                <div className="flex gap-1">
                  <button type="button" className="btn-ghost h-8 px-2 text-xs" onClick={() => setEditing({
                    ...t,
                    member_ids: t.member_ids || [],
                    leader_id: t.leader_id || '',
                  })}
                  >
                    Editar
                  </button>
                  <button type="button" className="btn-ghost h-8 w-8 p-0 text-[hsl(var(--danger))]" onClick={() => del(t)}>
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
              <div className="mt-3 text-xs text-muted-foreground">
                {(t.members || []).length} miembro{(t.members || []).length === 1 ? '' : 's'}
                {t.leader && <> · Líder: {t.leader.name || t.leader.email}</>}
              </div>
              <div className="mt-2 flex flex-wrap gap-1">
                {(t.members || []).slice(0, 8).map((m) => (
                  <span key={m.id} className="chip">{m.name || m.email}</span>
                ))}
                {(t.members || []).length > 8 && <span className="chip">+{(t.members || []).length - 8}</span>}
              </div>
            </div>
          ))}
        </div>
      )}

      {editing && (
        <div className="fixed inset-0 z-50 bg-black/40 grid place-items-center p-4" onClick={() => setEditing(null)}>
          <div onClick={(e) => e.stopPropagation()} className="surface w-full max-w-lg p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-display text-xl font-semibold">{editing.id ? 'Editar equipo' : 'Nuevo equipo'}</h2>
              <button type="button" onClick={() => setEditing(null)} className="btn-ghost h-8 w-8 p-0"><X className="h-4 w-4" /></button>
            </div>
            <div className="space-y-3 text-sm">
              <label className="block">Nombre
                <input className="input mt-1" value={editing.name || ''} onChange={(e) => setEditing({ ...editing, name: e.target.value })} />
              </label>
              <label className="block">Tipo
                <select className="input mt-1" value={editing.kind || 'team'} onChange={(e) => setEditing({ ...editing, kind: e.target.value })}>
                  <option value="team">Equipo</option>
                  <option value="branch">Sucursal</option>
                </select>
              </label>
              <label className="block">Descripción
                <input className="input mt-1" value={editing.description || ''} onChange={(e) => setEditing({ ...editing, description: e.target.value })} />
              </label>
              <label className="block">Líder
                <select className="input mt-1" value={editing.leader_id || ''} onChange={(e) => setEditing({ ...editing, leader_id: e.target.value })}>
                  <option value="">—</option>
                  {users.map((u) => <option key={u.id} value={u.id}>{u.name || u.email}</option>)}
                </select>
              </label>
              <div>
                <div className="text-xs font-medium text-muted-foreground mb-1">Miembros</div>
                <div className="max-h-48 overflow-y-auto border border-border rounded-[10px] p-2 space-y-1">
                  {users.map((u) => (
                    <label key={u.id} className="flex items-center gap-2 text-sm py-0.5">
                      <input
                        type="checkbox"
                        checked={(editing.member_ids || []).includes(u.id)}
                        onChange={() => toggleMember(u.id)}
                      />
                      <span>{u.name || u.email}</span>
                      <span className="text-[10px] text-muted-foreground">{u.role}</span>
                    </label>
                  ))}
                </div>
              </div>
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={editing.active !== false} onChange={(e) => setEditing({ ...editing, active: e.target.checked })} />
                Activo
              </label>
            </div>
            <div className="flex justify-end gap-2 mt-5">
              <button type="button" className="btn-ghost" onClick={() => setEditing(null)}>Cancelar</button>
              <button type="button" className="btn-primary" onClick={save}><Save className="h-4 w-4" /> Guardar</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
